/**
 * Main Slider Video Management and Swiper Initialization
 * Handles video playback for the main slider slides and Swiper setup
 */

/**
 * Initialize Swiper slider with video support
 */
export function initializeMainSlider(containerElement: HTMLElement | null): Promise<any> {
    return new Promise((resolve, reject) => {
    if (!containerElement) {
            reject(null);
            return;
    }

    // Wait for Swiper to be available
        const initSwiper = (): void => {
        if (typeof window.Swiper === 'undefined') {
            setTimeout(initSwiper, 100);
                return;
        }

            // Give a small delay to ensure DOM is fully rendered
            setTimeout(() => {
        try {
            const swiperInstance = new window.Swiper(containerElement, {
                slidesPerView: 1,
                loop: true,
                effect: 'fade',
                        fadeEffect: {
                            crossFade: true
                        },
                        speed: 1000,
                pagination: {
                    el: '#main-slider-pagination',
                    type: 'bullets',
                    clickable: true
                },
                navigation: {
                    nextEl: '#main-slider__swiper-button-next',
                    prevEl: '#main-slider__swiper-button-prev'
                },
                autoplay: {
                            delay: 8000,
                            disableOnInteraction: false
                },
                // Allow touch move on mobile for scrolling, but prevent slide changes
                allowTouchMove: true,
                simulateTouch: true,
                touchRatio: 0,
                touchAngle: 0,
                threshold: 1000,
                preventClicks: false,
                preventClicksPropagation: false,
                on: {
                    init: function(this: any) {
                        // Initialize video handlers after Swiper is ready
                        setTimeout(() => {
                            initializeSliderVideos(this);
                        }, 100);
                        
                        // Prevent clicks on slides from changing slides
                        // Allow clicks on pagination and interactive elements
                        const slides = containerElement.querySelectorAll('.swiper-slide');
                        slides.forEach((slide: Element) => {
                            slide.addEventListener('click', (e: Event) => {
                                const target = e.target as HTMLElement;
                                // Allow clicks on pagination, buttons, links, and other interactive elements
                                if (
                                    target.closest('#main-slider-pagination') ||
                                    target.closest('a') ||
                                    target.closest('button') ||
                                    target.closest('.thm-btn') ||
                                    target.closest('.slider-appointment-btn')
                                ) {
                                    return; // Allow the click to proceed normally
                                }
                                // Prevent slide change for other clicks
                                e.stopPropagation();
                            });
                        });
                            },
                            slideChange: function(this: any) {
                    }
                }
            });

                    resolve(swiperInstance);
        } catch (error) {
                    reject(error);
        }
            }, 100);
    };

        // Start initialization
        initSwiper();
        });
}

/**
 * Initialize slider videos for video playback management
 * This matches the original HTML script implementation
 */
export function initializeSliderVideos(swiperInstance: any) {
    if (!swiperInstance) {
        return;
    }

    // Load and prepare all videos, especially important for mobile
    const allVideos = document.querySelectorAll<HTMLVideoElement>('.main-slider__video-bg');
    const isMobile = window.innerWidth <= 991;
    const activeSlideIndex = swiperInstance.activeIndex;
    
    allVideos.forEach((video, index) => {
        const isActiveVideo = index === activeSlideIndex;
        // Set mobile-specific attributes
        if (isMobile) {
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');
            video.setAttribute('x5-playsinline', 'true');
            video.setAttribute('x5-video-player-type', 'h5');
            video.setAttribute('x5-video-player-fullscreen', 'false');
        }
        
        // Force load video - critical for proper loading
        video.load();
        
        // Set up load event to ensure video is ready
        const handleLoadedData = () => {
            // On mobile, ensure video metadata is loaded
            if (isMobile && video.readyState >= 2) {
                video.currentTime = 0;
            }
        };
        video.addEventListener('loadeddata', handleLoadedData, { once: true });
        
        // Set up canplay event for better support
        const handleCanPlay = () => {
            // Video is ready to play
        };
        video.addEventListener('canplay', handleCanPlay, { once: true });
        
        // Track loadedmetadata
        video.addEventListener('loadedmetadata', () => {
            // Metadata loaded
        }, { once: true });
        
        // Set up error handler with retry
        const handleError = (_e: Event) => {
            // Retry loading
            if (isMobile && index < 3) {
                setTimeout(() => {
                    video.load();
                }, 1000 * (index + 1));
            } else if (!isMobile) {
                // Desktop: retry once after shorter delay
                setTimeout(() => {
                    video.load();
                }, 500);
            }
        };
        video.addEventListener('error', handleError, { once: true });
        
        // On desktop, don't pause the active slide's video - let autoplay work
        // On mobile, pause all videos (will be played by playVideoInSlide)
        if (isMobile || !isActiveVideo) {
            video.pause();
            video.currentTime = 0;
        } else {
            // Desktop active video: ensure it plays immediately
            // Don't pause, let autoplay work
            if (video.readyState >= 1) {
                // Video has metadata, try to play immediately
                video.play()
                    .then(() => {
                        // Play succeeded
                    })
                    .catch(_err => {
                        // Play failed, will retry
                    });
            } else {
                // Wait for metadata then play
                const playWhenReady = () => {
                    if (video.readyState >= 1) {
                        video.play()
                            .then(() => {
                                // Play succeeded after metadata
                            })
                            .catch(_err => {
                                // Play failed after metadata
                            });
                    } else {
                        video.addEventListener('loadedmetadata', playWhenReady, { once: true });
                    }
                };
                playWhenReady();
            }
        }
    });

    // Function to pause all videos (optionally skip active video on desktop or skip specific video index)
    function pauseAllVideos(skipActiveOnDesktop: boolean = false, skipVideoIndex: number | null = null) {
        const allVideos = document.querySelectorAll<HTMLVideoElement>('.main-slider__video-bg');
        const isMobile = window.innerWidth <= 991;
        const activeSlideIndex = swiperInstance.activeIndex;
        
        allVideos.forEach((video, index) => {
            // Skip pausing if this is the video we want to keep playing
            if (skipVideoIndex !== null && index === skipVideoIndex) {
                return;
            }
            
            // On desktop, skip pausing the active video if requested
            if (skipActiveOnDesktop && !isMobile && index === activeSlideIndex) {
                return; // Don't pause the active video on desktop
            }
            if (!video.paused) {
                video.pause();
            }
        });
    }

    // Function to recalculate video dimensions
    function recalculateVideoDimensions(video: HTMLVideoElement, slide: Element) {
        const slideElement = slide as HTMLElement;
        if (slideElement) {
            // Ensure video covers the full slide
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.left = '0';
            video.style.top = '0';
            video.style.transform = 'none';
        }
    }

    // Function to play video in a slide
    function playVideoInSlide(slide: Element) {
        const video = slide.querySelector<HTMLVideoElement>('.main-slider__video-bg');
        if (video) {
            const slideIndex = Array.from(slide.parentElement?.children || []).indexOf(slide);
            
            // Recalculate dimensions first
            recalculateVideoDimensions(video, slide);
            
            // Pause all videos EXCEPT the one we're about to play
            pauseAllVideos(false, slideIndex);
            
            // Check if video needs to be loaded
            const needsLoad = video.readyState < 2 || video.readyState === 0;
            
            if (needsLoad) {
                // Wait for video to load before playing
                const playAfterLoad = () => {
                    // Reset to start
                    video.currentTime = 0;
                    
                    // Play the video
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                // Recalculate dimensions after video starts playing
                                setTimeout(() => recalculateVideoDimensions(video, slide), 100);
                            })
                            .catch(_error => {
                                // Play failed
                            });
                    }
                };
                
                // Set up event listeners for when video is ready
                const handleCanPlay = () => {
                    playAfterLoad();
                    video.removeEventListener('canplay', handleCanPlay);
                    video.removeEventListener('loadeddata', handleLoadedData);
                };
                
                const handleLoadedData = () => {
                    if (video.readyState >= 2) {
                        playAfterLoad();
                        video.removeEventListener('canplay', handleCanPlay);
                        video.removeEventListener('loadeddata', handleLoadedData);
                    }
                };
                
                video.addEventListener('canplay', handleCanPlay, { once: true });
                video.addEventListener('loadeddata', handleLoadedData, { once: true });
                
                // Start loading
                video.load();
            } else {
                // Video is already loaded, play immediately
                
                // Reset to start
                video.currentTime = 0;
                
                // Play the video
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            // Recalculate dimensions after video starts playing
                            setTimeout(() => recalculateVideoDimensions(video, slide), 100);
                        })
                        .catch(_error => {
                            // Play failed
                        });
                }
            }
        }
    }


    // Pause all videos to prevent background playback
    // On desktop, don't pause the active video - let it autoplay
    pauseAllVideos(!isMobile); // Skip active video on desktop

    // Play video in the initial active slide
    const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
    
    if (activeSlide) {
        // Unlock page immediately for both mobile and desktop
        document.body.classList.remove('page-loading');
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        
        const homePage = document.querySelector('.home-page');
        if (homePage) {
            homePage.classList.remove('home-page--loading');
        }
        
        // Play video immediately - different approach for desktop vs mobile
        const video = activeSlide.querySelector('.main-slider__video-bg') as HTMLVideoElement | null;
        
        if (video) {
            if (isMobile) {
                // Mobile: wait for video to be ready (works well, don't change)
                if (video.readyState >= 2) {
                    playVideoInSlide(activeSlide);
                } else {
                    const handleCanPlay = () => {
                        playVideoInSlide(activeSlide);
                        video.removeEventListener('canplay', handleCanPlay);
                    };
                    video.addEventListener('canplay', handleCanPlay, { once: true });
                    setTimeout(() => {
                        if (video.readyState >= 2) {
                            playVideoInSlide(activeSlide);
                        }
                    }, 50);
                }
            } else {
                // Desktop: play immediately, very aggressive approach
                // Video should already be playing from autoplay, but ensure it continues
                
                const tryPlayNow = () => {
                    // If video is already playing, don't interfere
                    if (!video.paused) {
                        return;
                    }
                    
                    // Don't try to play if video is loading (readyState === 0)
                    // Wait for it to load first
                    if (video.readyState === 0) {
                        return;
                    }
                    
                    // Try to play immediately
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                // Play succeeded
                            })
                            .catch(error => {
                                // Only retry if it's not an abort error (which happens when load() interrupts)
                                if (error.name !== 'AbortError') {
                                    // Retry after short delay
                                    setTimeout(() => {
                                        if (video.readyState > 0 && video.paused) {
                                            video.play().catch(() => {});
                                        }
                                    }, 200);
                                }
                            });
                    }
                };
                
                // Set up event listeners first
                const handleCanPlay = () => {
                    tryPlayNow();
                    video.removeEventListener('canplay', handleCanPlay);
                    video.removeEventListener('loadeddata', handleLoadedData);
                };
                
                const handleLoadedData = () => {
                    if (video.readyState >= 2) {
                        tryPlayNow();
                        video.removeEventListener('canplay', handleCanPlay);
                        video.removeEventListener('loadeddata', handleLoadedData);
                    }
                };
                
                // Try to play immediately if video is ready
                if (video.readyState >= 1) {
                    tryPlayNow();
                } else {
                    video.addEventListener('canplay', handleCanPlay, { once: true });
                    video.addEventListener('loadeddata', handleLoadedData, { once: true });
                    
                    // Force load if needed
                    if (video.readyState === 0) {
                        video.load();
                    }
                }
                
                // Fallback attempts (only if video is ready and paused)
                setTimeout(() => {
                    if (video.readyState >= 1 && video.paused) {
                        tryPlayNow();
                    }
                }, 100);
                setTimeout(() => {
                    if (video.readyState >= 1 && video.paused) {
                        tryPlayNow();
                    }
                }, 300);
            }
        } else {
            // No video found, try to play anyway
            playVideoInSlide(activeSlide);
        }
    }

    // Handle slide changes - pause previous video when transition starts
    swiperInstance.on('slideChangeTransitionStart', function(this: any) {
        // Pause all videos first
        pauseAllVideos();
    });

    // Handle slide changes - play new video when transition ends
    swiperInstance.on('slideChangeTransitionEnd', function(this: any) {
        // Play video in the new active slide
        const activeSlide = this.slides[this.activeIndex];
        if (activeSlide) {
            playVideoInSlide(activeSlide);
        }
    });

    // Enable videos on first user interaction (helps with autoplay policies)
    const enableVideosOnInteraction = () => {
        const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
        if (activeSlide) {
            playVideoInSlide(activeSlide);
        }
        // Remove listeners after first interaction
        document.removeEventListener('click', enableVideosOnInteraction);
        document.removeEventListener('touchstart', enableVideosOnInteraction);
    };

    document.addEventListener('click', enableVideosOnInteraction, { once: true });
    document.addEventListener('touchstart', enableVideosOnInteraction, { once: true });

    // Handle window resize to fix video dimensions
    let resizeTimeout: number | null = null;
    function handleResize() {
        // Debounce resize events
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        
        resizeTimeout = window.setTimeout(() => {
            const allVideos = document.querySelectorAll<HTMLVideoElement>('.main-slider__video-bg');
            const slides = document.querySelectorAll('.main-slider .swiper-slide');
            
            allVideos.forEach((video, index) => {
                const slide = slides[index];
                if (slide) {
                    recalculateVideoDimensions(video, slide);
                    
                    // Force video to recalculate its display
                    if (video.readyState >= 2) {
                        // Video is loaded, trigger a reflow
                        video.style.display = 'none';
                        video.offsetHeight; // Trigger reflow
                        video.style.display = '';
                    }
                }
            });
            
        }, 250) as unknown as number;
    }
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Also handle orientation change
    window.addEventListener('orientationchange', () => {
        setTimeout(handleResize, 500);
    });
}
